import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { WarningCircle } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportRowStatus } from 'bom/enums';
import { useGetBomImportParts } from 'bom/hooks';
import { Button } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { Color, Level, Variant } from 'theme/enums';

interface IProps {
  importId: number | null;
  onClose: () => void;
}

const FAILED_ROWS_PAGE_SIZE = 100;

export const ImportFailedRowsModal = ({ importId, onClose }: IProps) => {
  const { t } = useTranslation();
  const { showErrorNotification } = useNotificationContext();

  const { records, count, isLoading, isFetchingNextPage, hasNextPage, isError } =
    useGetBomImportParts(
      {
        partImport: importId ?? 0,
        importStatus: ImportRowStatus.FAILED,
        pageSize: FAILED_ROWS_PAGE_SIZE,
      },
      Boolean(importId)
    );

  const isLoadingRows = isLoading || isFetchingNextPage || Boolean(hasNextPage);
  const failedCount = count ?? records?.length ?? 0;

  useEffect(() => {
    if (isError) {
      showErrorNotification(
        { title: t('errorTitleImportFailedRowsFetch') },
        NotificationPosition.TOP
      );
    }
  }, [isError, showErrorNotification, t]);

  useEffect(() => {
    if (importId && !isLoadingRows && (isError || failedCount === 0)) {
      onClose();
    }
  }, [importId, isLoadingRows, isError, failedCount, onClose]);

  if (!importId || isLoadingRows || isError || failedCount === 0) {
    return null;
  }

  return (
    <Modal open onClose={onClose}>
      <ModalDialog color={Color.WARNING} variant={Variant.OUTLINED} sx={{ maxWidth: 720 }}>
        <DialogTitle>
          <WarningCircle weight="fill" />
          {t('titleImportFailedRows')}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <Typography level={Level.BODY_MD}>
              {t('messageImportFailedRows', { count: failedCount })}
            </Typography>
            <Sheet
              variant={Variant.OUTLINED}
              sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 360 }}
            >
              <Table
                size="sm"
                stickyHeader
                sx={{
                  tableLayout: 'auto',
                  '& th': { fontWeight: 600, backgroundColor: 'background.level1' },
                  '& th, & td': { padding: '6px 10px', verticalAlign: 'top' },
                }}
              >
                <thead>
                  <tr>
                    <th>{t('headerPartNumber')}</th>
                    <th>{t('headerManufacturer')}</th>
                    <th>{t('headerCustomerPartNumber')}</th>
                    <th>{t('labelErrorMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {records?.map((row) => (
                    <tr key={row.id}>
                      <td>{row.number || DEFAULT_PLACEHOLDER}</td>
                      <td>{row.manufacturer || DEFAULT_PLACEHOLDER}</td>
                      <td>{row.customerPartNumber || DEFAULT_PLACEHOLDER}</td>
                      <td>{row.errorMessage || DEFAULT_PLACEHOLDER}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flex: 'none' }}>
          <Button text={t('buttonLabelClose')} variant={Variant.SOLID} onClick={onClose} />
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
